import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSearch, FaTimes, FaSpinner } from "react-icons/fa";
import { searchLocalContent } from "./siteSearchIndex";
import { searchArticles } from "../services/blogService";
import { searchEvents } from "../services/eventService";
import "./SiteSearch.css";

const MIN_CHARS = 2;

/**
 * Site-wide search overlay opened from the header magnifier.
 * Combines instant local matches (pages + programmes) with
 * server-side article/event search.
 */
export default function SiteSearch({ open, onClose }) {
  const [term, setTerm] = useState("");
  const [remote, setRemote] = useState({ articles: [], events: [] });
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const local = useMemo(
    () => (term.trim().length >= MIN_CHARS ? searchLocalContent(term) : { pages: [], programmes: [], faqs: [] }),
    [term]
  );

  // Reset + focus whenever the overlay opens.
  useEffect(() => {
    if (!open) return;
    setTerm("");
    setRemote({ articles: [], events: [] });
    setActiveIndex(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 60);
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(id);
      document.body.style.overflow = "";
    };
  }, [open]);

  // Debounced remote search for articles + events.
  useEffect(() => {
    if (!open) return undefined;
    const query = term.trim();
    if (query.length < MIN_CHARS) {
      setRemote({ articles: [], events: [] });
      setLoading(false);
      return undefined;
    }

    let canceled = false;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      const [articles, events] = await Promise.all([
        searchArticles(query, 5).catch(() => []),
        searchEvents(query, 5).catch(() => []),
      ]);
      if (canceled) return;
      setRemote({ articles: articles || [], events: events || [] });
      setLoading(false);
    }, 280);

    return () => {
      canceled = true;
      window.clearTimeout(timer);
      setLoading(false);
    };
  }, [term, open]);

  const results = useMemo(() => {
    const rows = [];
    local.programmes.forEach((p) =>
      rows.push({ key: `prog-${p.slug}`, group: "Programmes", title: p.title, meta: p.school, to: `/programme/${p.slug}` })
    );
    local.pages.forEach((p) => rows.push({ key: `page-${p.path}`, group: "Pages", title: p.title, meta: p.path, to: p.path }));
    (local.faqs || []).forEach((f) =>
      rows.push({ key: `faq-${f.question}`, group: "FAQs", title: f.question, meta: f.category, to: "/faqs" })
    );
    remote.articles.forEach((a) =>
      rows.push({ key: `art-${a.id}`, group: "Articles", title: a.title, meta: a.category?.name || "Blog", to: `/blog/${a.slug}` })
    );
    remote.events.forEach((e) =>
      rows.push({ key: `ev-${e.id}`, group: "Events", title: e.title, meta: e.location || "Event", to: `/events/${e.slug}` })
    );
    return rows;
  }, [local, remote]);

  useEffect(() => {
    setActiveIndex(0);
  }, [results.length]);

  if (!open) return null;

  const go = (to) => {
    onClose();
    navigate(to);
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (!results.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      go(results[activeIndex].to);
    }
  };

  const groups = results.reduce((acc, row) => {
    (acc[row.group] = acc[row.group] || []).push(row);
    return acc;
  }, {});

  const showEmpty = term.trim().length >= MIN_CHARS && !loading && results.length === 0;

  return (
    <div className="site-search" role="presentation" onKeyDown={onKeyDown}>
      <div className="site-search-backdrop" onClick={onClose} aria-hidden="true" />

      <div className="site-search-panel" role="dialog" aria-modal="true" aria-label="Search Zetech University">
        <div className="site-search-field">
          <FaSearch aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search programmes, pages, articles and events…"
            aria-label="Search Zetech University"
            autoComplete="off"
          />
          {loading && <FaSpinner className="site-search-spin" aria-hidden="true" />}
          <button type="button" className="site-search-close" onClick={onClose} aria-label="Close search">
            <FaTimes />
          </button>
        </div>

        <div className="site-search-results" role="listbox" aria-label="Search results">
          {term.trim().length < MIN_CHARS && (
            <p className="site-search-hint">Type at least {MIN_CHARS} characters. Use ↑ ↓ to browse, Enter to open, Esc to close.</p>
          )}

          {showEmpty && (
            <p className="site-search-hint">
              No results for “{term.trim()}”. Try “diploma”, “fees”, “application” or “graduation”.
            </p>
          )}

          {Object.entries(groups).map(([group, rows]) => (
            <div className="site-search-group" key={group}>
              <h3>{group}</h3>
              {rows.map((row) => {
                const index = results.indexOf(row);
                return (
                  <button
                    type="button"
                    key={row.key}
                    role="option"
                    aria-selected={index === activeIndex}
                    className={`site-search-result ${index === activeIndex ? "is-active" : ""}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => go(row.to)}
                  >
                    <span className="site-search-result-title">{row.title}</span>
                    {row.meta && <span className="site-search-result-meta">{row.meta}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
